import {
	Ellipsis,
	Folder,
	Info,
	WarningLight,
} from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import FavouriteButton from '@/components/shared/FavouriteButton/FavouriteButton'
import MoreOptions from '@/components/shared/TrackRow/MoreOptions/MoreOptions'
import { useOnScreen } from '@/hooks/useOnScreen'
import sessionStore from '@/stores/sessionStore'
import { isTrialCustomer } from '@/utils/user/isTrialCustomer'

import AddToCart from './AddToCart/AddToCart'
import AddToProject from './AddToProject/AddToProject'
import Download from './Download/Download'
import { DownloadOffScreen } from './Download/Download'
import License from './License/License'
import Note from './Note/Note'
import NotePopover from './NotePopover/NotePopover'
import SimilarTracks from './SimilarTracks/SimilarTracks'
import {
	CART_ACTION,
	DOWNLOAD_MP3_ACTION,
	FAVOURITES_ACTION,
	MORE_OPTIONS_ACTION,
	NOTES_ACTION,
	PROJECTS_ACTION,
	SIMILAR_TRACKS_ACTION,
	TRACK_TRAY_ACTION,
} from './TrackActions.constants'
import { useTrackActions } from './TrackActions.hooks'
import styles from './TrackActions.module.css'
import { TrackActionId, TrackActionsProps } from './TrackActions.types'
import TrackTray from './TrackTray/TrackTray'

const TrackActions = ({
	track,
	source,
	location,
	include,
}: TrackActionsProps): JSX.Element => {
	const { locale } = useRouter()
	const user = sessionStore((state) => state.session?.user)
	const showDownloadOptions = !!user && !isTrialCustomer(user)
	const { t } = useTranslation(['trackrow'])
	const { userActions, canViewTrackCatalogue, trackOptions, projectActions } =
		useTrackActions({
			track,
			source,
		})

	const actionsWrapperRef = useRef<HTMLDivElement>(null)
	const isOnScreen = useOnScreen(actionsWrapperRef)

	const NotePopoverComponent = (): JSX.Element =>
		isOnScreen ? <NotePopover id={`note-${track.id}`} track={track} /> : <></>

	const TrackTrayComponent = (): JSX.Element =>
		isOnScreen ? (
			<TrackTray track={track} source={source} />
		) : (
			<Info className={styles.trackAction} />
		)

	const AddToProjectComponent = (
		props: React.HTMLAttributes<HTMLButtonElement>
	): JSX.Element =>
		isOnScreen ? (
			<AddToProject track={track} source={source} {...props} />
		) : (
			<Folder className={styles.trackAction} {...props} />
		)

	const DownloadComponent = (
		props: React.HTMLAttributes<HTMLSpanElement>
	): JSX.Element =>
		isOnScreen ? (
			<Download
				source={source}
				location={location}
				track={track}
				zipFileName={track.title}
				showDownloadOptions={showDownloadOptions}
				{...props}
			/>
		) : (
			<DownloadOffScreen showDownloadOptions={showDownloadOptions} {...props} />
		)

	const MoreOptionsComponent = (): JSX.Element =>
		isOnScreen ? <MoreOptions track={track} source={source} /> : <Ellipsis />

	const shouldInclude = (actionId: TrackActionId): boolean =>
		!include || include.includes(actionId)

	return (
		<div
			className={styles.trackActions}
			data-testid="track-actions-prototype"
			ref={actionsWrapperRef}
		>
			{shouldInclude(NOTES_ACTION) &&
				projectActions.some((option) => option.id === NOTES_ACTION) && (
					<>
						<Note className={styles.trackAction} track={track} />
						<NotePopoverComponent />
					</>
				)}
			{shouldInclude(SIMILAR_TRACKS_ACTION) &&
				trackOptions.some((option) => option.id === SIMILAR_TRACKS_ACTION) && (
					<SimilarTracks
						className={styles.trackAction}
						track={track}
						source={source}
					/>
				)}
			{shouldInclude(TRACK_TRAY_ACTION) && <TrackTrayComponent />}
			{/* Always show when signed out — not gated by shouldInclude/include allowlists */}
			{!user && locale !== 'ja-JP' && (
				<License
					className={clsx(styles.trackAction, styles.download)}
					trackId={track.id}
					source={source}
				/>
			)}
			{shouldInclude(FAVOURITES_ACTION) &&
				userActions.some((action) => action.id === FAVOURITES_ACTION) && (
					<FavouriteButton
						className={styles.trackAction}
						source={source}
						track={track}
					/>
				)}
			{shouldInclude(PROJECTS_ACTION) &&
				userActions.some((action) => action.id === PROJECTS_ACTION) && (
					<AddToProjectComponent className={styles.trackAction} />
				)}
			{shouldInclude(DOWNLOAD_MP3_ACTION) &&
				userActions.some((action) => action.id === DOWNLOAD_MP3_ACTION) && (
					<DownloadComponent
						className={clsx(styles.trackAction, styles.download)}
					/>
				)}
			{shouldInclude(CART_ACTION) &&
				!!user?.organisation.canPurchase &&
				userActions.some((action) => action.id === CART_ACTION) && (
					<AddToCart
						className={styles.trackAction}
						track={track}
						source={source}
					/>
				)}
			{shouldInclude(MORE_OPTIONS_ACTION) &&
				(!canViewTrackCatalogue ? (
					<WarningLight
						id={`track-unavailable-${track.id}`}
						title={t('track-actions.track-unavailable')}
						data-testid="track-unavailable-icon"
					/>
				) : (
					<MoreOptionsComponent />
				))}
		</div>
	)
}

export default TrackActions
